/**
 * Usuarios.tsx — la gente que entra a ESTOS libros.
 *
 * Hasta hoy esta ruta llevaba a «en construcción, etapa E6», y no era un
 * detalle pendiente: **cada usuario nuevo de cada cliente pasaba por
 * nosotros**. E8 automatizó invitar desde la consola de operador, que es
 * nuestra silla; el cliente que contrata a una contadora tenía que escribirnos
 * y esperar. No pasa una vez, pasa cada vez que entra o sale alguien.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LA PANTALLA NO DECIDE QUIÉN PUEDE. LO DECIDE LA BASE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un contador ve la lista y no ve los botones. Eso es comodidad, **no es el
 * control**: `invitar_a_la_empresa` exige `app.rol = 'admin'` y rechaza con
 * SB130 venga de donde venga. Si algún día la pantalla y la base discrepan, la
 * que manda es la base y la pantalla está mintiendo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ «INVITADA» Y «ENTRÓ» SON DOS COSAS, Y SE VEN DISTINTAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un correo de invitación se pierde, se va a spam, o la persona lo abre tres
 * semanas después. Sin distinguirlo, el admin mira una lista donde todos se
 * ven igual y no tiene forma de saber a quién reenviárselo — que es justo la
 * pregunta que lo trajo a esta pantalla.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y EL ÚLTIMO ADMIN NO SE PUEDE QUITAR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La base lo rechaza con SB132. Acá además se apaga el botón, porque el error
 * llega después de haber hecho clic y esta pantalla es de las que se usan
 * apuradas. Es un estado sin salida desde dentro del producto: una empresa sin
 * admin no puede volver a invitar a nadie, ni siquiera a un admin.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import {
  cambiarRol, invitarAMiEmpresa, sacarDeMiEmpresa, traerMiembrosDeMiEmpresa,
  FalloDeApi,
  type MiembroReal,
} from '../datos/libros.ts';
import type { Clave } from '../nucleo/idioma.ts';
import { ROLES, rotuloDeRol } from '../nucleo/roles.ts';


export function Usuarios() {
  const { t, fecha } = useIdioma();
  const { pedir, sesion } = useSesion();

  const soyAdmin = sesion?.rol === 'admin';
  const empresaId = sesion?.empresa.id ?? null;

  const [miembros, setMiembros] = useState<MiembroReal[] | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [correo, setCorreo] = useState('');
  const [nombre, setNombre] = useState('');
  /** Sin valor por omisión: ver SB113. Dar de más no se queja nadie nunca. */
  const [rol, setRol] = useState('');

  const decir = useCallback((e: unknown) => {
    const f = e instanceof FalloDeApi ? e : null;
    setFallo(f?.clase === 'conexion' ? 'sb.conexion'
      : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
  }, []);

  const mirar = useCallback(async () => {
    try {
      setMiembros(await traerMiembrosDeMiEmpresa(pedir));
    } catch (e) {
      setMiembros([]);
      decir(e);
    }
  }, [pedir, decir]);

  useEffect(() => { if (empresaId) void mirar(); }, [empresaId, mirar]);

  /** Cuántos admin quedan. Decide si se puede quitar o rebajar a alguien. */
  const admins = (miembros ?? []).filter((m) => m.rol === 'admin').length;

  async function invitar(ev: FormEvent) {
    ev.preventDefault();
    setOcupado(true);
    setFallo(null);
    setAviso(null);
    try {
      const r = await invitarAMiEmpresa(pedir, { correo: correo.trim(), nombre, rol });
      setCorreo('');
      setNombre('');
      setRol('');
      // ▶ Las dos mitades se cuentan por separado.
      //
      // La membresía es lo que decide si la persona puede entrar; el correo es
      // solo cómo se entera. Decir «invitada» cuando el correo no salió deja al
      // admin esperando una respuesta que nunca va a llegar.
      setAviso(r.invitado ? 'us.invited' : `us.noMail.${r.motivo ?? 'rechazada'}`);
      await mirar();
    } catch (e) {
      decir(e);
    } finally {
      setOcupado(false);
    }
  }

  async function cambiar(m: MiembroReal, nuevo: string) {
    setOcupado(true);
    setFallo(null);
    setAviso(null);
    try {
      await cambiarRol(pedir, { correo: m.correo, rol: nuevo });
      await mirar();
    } catch (e) {
      decir(e);
      // Se vuelve a leer: la base deshizo el cambio y la pantalla tiene que
      // enseñar lo que quedó, no lo que se intentó.
      await mirar();
    } finally {
      setOcupado(false);
    }
  }

  async function sacar(m: MiembroReal) {
    setOcupado(true);
    setFallo(null);
    setAviso(null);
    try {
      await sacarDeMiEmpresa(pedir, m.correo);
      await mirar();
    } catch (e) {
      decir(e);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div>
      <header className="page-head">
        <div>
          <h1>{t('us.title')}</h1>
          <p className="small muted">{t('us.sub')}</p>
        </div>
      </header>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as Clave)}</span>
        </div>
      )}
      {aviso && (
        <div className="alert" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(aviso as Clave)}</span>
        </div>
      )}

      {/* ── Invitar. Solo el admin lo ve; la base lo exige igual. ── */}
      {soyAdmin && (
        <section className="card" style={{ marginBottom: 'var(--s6)' }}>
          <div className="card__head"><h3>{t('us.invite')}</h3></div>
          <form onSubmit={invitar}>
            <div className="grid grid--3" style={{ gap: 'var(--s5)' }}>
              <label className="field">
                <span className="field__label">{t('us.email')}</span>
                <input
                  className="input" type="email" required value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                />
                <span className="field__hint">{t('us.emailHint')}</span>
              </label>
              <label className="field">
                <span className="field__label">{t('us.name')}</span>
                <input
                  className="input" required value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field__label">{t('us.role')}</span>
                <select className="input" value={rol} onChange={(e) => setRol(e.target.value)}>
                  {/* En blanco a propósito: elegir el rol es la decisión, no un
                      trámite. Un defecto acá sería indistinguible de una
                      respuesta — cambio 79. */}
                  <option value="">{t('us.pickRole')}</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{t(rotuloDeRol(r))}</option>
                  ))}
                </select>
                <span className="field__hint">{t('us.roleHint')}</span>
              </label>
            </div>
            <div className="page-actions" style={{ marginTop: 'var(--s5)' }}>
              <button
                className="btn btn--primary" type="submit"
                disabled={ocupado || rol === '' || correo.trim() === ''}
              >
                {t('us.send')}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="card card--pad0">
        <div className="card__head">
          <h3>{t('us.who')}</h3>
          <span className="spacer" />
          <span className="small muted">
            {miembros === null
              ? ''
              : miembros.length === 1
                ? t('us.countOne')
                : t('us.count', { personas: String(miembros.length) })}
          </span>
        </div>

        {miembros === null ? (
          <p className="muted small" style={{ padding: 'var(--s7)' }}>{t('common.loading')}</p>
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('us.person')}</th>
                  <th>{t('us.role')}</th>
                  <th>{t('us.since')}</th>
                  {soyAdmin && <th />}
                </tr>
              </thead>
              <tbody>
                {miembros.map((m) => {
                  // El último admin no se puede quitar ni rebajar: la empresa
                  // se quedaría sin nadie que pueda invitar.
                  const ultimoAdmin = m.rol === 'admin' && admins === 1;
                  return (
                    <tr key={m.correo}>
                      <td>
                        {m.nombre}
                        {m.soy_yo && <span className="chip chip--plain">{t('us.you')}</span>}
                        <br />
                        <span className="small muted mono">{m.correo}</span>
                        {!m.ligado && (
                          <span className="chip chip--warn" title={t('us.pendingWhy')}>
                            {t('us.pending')}
                          </span>
                        )}
                      </td>
                      <td>
                        {soyAdmin && !ultimoAdmin ? (
                          <select
                            className="input" value={m.rol} disabled={ocupado}
                            onChange={(e) => void cambiar(m, e.target.value)}
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>{t(rotuloDeRol(r))}</option>
                            ))}
                          </select>
                        ) : (
                          <>
                            {t(rotuloDeRol(m.rol))}
                            {ultimoAdmin && (
                              <span className="small muted"> · {t('us.lastAdmin')}</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="small">{fecha(m.desde.slice(0, 10))}</td>
                      {soyAdmin && (
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn--ghost btn--sm" type="button"
                            disabled={ocupado || ultimoAdmin}
                            title={ultimoAdmin ? t('us.lastAdminWhy') : undefined}
                            onClick={() => void sacar(m)}
                          >
                            {t('us.remove')}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="card__foot">
          <span className="small muted">{t('us.foot')}</span>
        </div>
      </section>
    </div>
  );
}
